import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  Box,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Paper,
  useMediaQuery,
  useTheme,
  Card,
  Chip,
} from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import api from "../../utils/api";
import { Autocomplete } from "@mui/material";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import FilterListIcon from "@mui/icons-material/FilterList";
import SearchIcon from "@mui/icons-material/Search";
import RestartAltIcon from "@mui/icons-material/RestartAlt";

const ViolationsExportPage = () => {
  const [status, setStatus] = useState("");
  const [statuses, setStatuses] = useState([]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [propertyId, setPropertyId] = useState("");
  const [membershipType, setMembershipType] = useState("");
  const [membershipTypeOptions, setMembershipTypeOptions] = useState([]);
  const [membershipTypeLoading, setMembershipTypeLoading] = useState(false);
  const [violationType, setViolationType] = useState("");
  const [violationTypeOptions, setViolationTypeOptions] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);

  const printRef = useRef(null);
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down("md"));

  const pageCardSx = {
    borderRadius: "18px",
    border: "1px solid rgba(47,99,173,0.08)",
    boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
    backgroundColor: "rgba(255,255,255,0.96)",
  };

  const softCardSx = {
    borderRadius: "16px",
    border: "1px solid rgba(47,99,173,0.08)",
    boxShadow: "0 4px 14px rgba(0,0,0,0.05)",
    backgroundColor: "#fff",
  };

  const sectionIconWrapSx = {
    width: 40,
    height: 40,
    minWidth: 40,
    borderRadius: "12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(47,99,173,0.08)",
    flexShrink: 0,
  };

  const primaryButtonSx = {
    backgroundColor: "#2f63ad",
    color: "white",
    fontWeight: 700,
    borderRadius: "10px",
    textTransform: "none",
    px: 2.25,
    minHeight: 44,
    boxShadow: "none",
    "&:hover": {
      backgroundColor: "#1f4f94",
      boxShadow: "none",
    },
  };

  const secondaryButtonSx = {
    borderRadius: "10px",
    textTransform: "none",
    fontWeight: 600,
    px: 2,
    minHeight: 44,
    borderColor: "rgba(47,99,173,0.18)",
    color: "#223142",
    "&:hover": {
      borderColor: "#2f63ad",
      color: "#2f63ad",
      backgroundColor: "rgba(47,99,173,0.04)",
    },
  };

  const textFieldSx = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "rgba(255,255,255,0.98)",
      borderRadius: "10px",
    },
    "& .MuiInputLabel-root": {
      color: "#6b778c",
    },
  };

  // Helpers
  const safeDate = (d) => (d ? new Date(d).toLocaleDateString() : "");

  const getMemberFullName = (m) => {
    if (!m) return "";
    const first = (m.FamiliarName || m.FirstName || "").trim();
    const last = (m.LastName || "").trim();
    return `${first} ${last}`.trim();
  };

  const getMemberFullAddress = (m) =>
    m?.FullAddress || m?.AlternateAddress || "";

  const getCureByDate = (violated_at, days_to_cure) => {
    if (!violated_at || !days_to_cure) return null;
    const start = new Date(violated_at);
    const deadline = new Date(start);
    deadline.setDate(start.getDate() + Number(days_to_cure));
    return deadline;
  };

  const getResolutionDelta = (cureBy, resolvedAt) => {
    if (!cureBy || !resolvedAt) return null;
    const diffMs = new Date(resolvedAt) - new Date(cureBy);
    const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const buildMembershipTypeOptionsFromRows = (rows) => {
    const uniq = new Set(
      (rows || [])
        .map((r) => r?.member?.ClubMembershipTypeCode)
        .filter(Boolean)
        .map((v) => String(v).trim())
    );
    return [...uniq].sort().map((code) => ({ value: code, label: code }));
  };

  const fetchMembershipTypeOptions = async () => {
    setMembershipTypeLoading(true);
    try {
      const params = new URLSearchParams();
      if (status) params.append("status", status);
      if (startDate) params.append("start", startDate);
      if (endDate) params.append("end", endDate);
      if (propertyId.trim()) params.append("property_id", propertyId.trim());
      params.append("includeMember", "1");

      const { data } = await api.get(`/getViolationsReport?${params.toString()}`);
      setMembershipTypeOptions(buildMembershipTypeOptionsFromRows(data || []));

      const { data: types } = await api.get(`/getCcrViolationTypes`);
      setViolationTypeOptions(
        (types || []).map((t) => ({
          value: String(t.id),
          label: t.title,
        }))
      );
    } catch (e) {
      console.error("membership type options fetch error:", e);
      setMembershipTypeOptions([]);
      setViolationTypeOptions([]);
    } finally {
      setMembershipTypeLoading(false);
    }
  };

  useEffect(() => {
    fetchMembershipTypeOptions();
    api
      .get("/statuses/violations")
      .then((res) => setStatuses(res.data || []))
      .catch((err) => console.error("Error fetching statuses:", err));
  }, []);

  useEffect(() => {
    if (filteredData?.length) {
      setMembershipTypeOptions(buildMembershipTypeOptionsFromRows(filteredData));
    }
  }, [filteredData]);

  const handleReset = () => {
    setStatus("");
    setStartDate("");
    setEndDate("");
    setPropertyId("");
    setViolationType("");
    setMembershipType("");
    setFilteredData([]);
  };

  const handleSearch = async () => {
    if (startDate && endDate && new Date(startDate) > new Date(endDate)) {
      alert("Start Date must be before End Date.");
      return;
    }

    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (status) params.append("status", status);
      if (startDate) params.append("start", startDate);
      if (endDate) params.append("end", endDate);
      if (propertyId.trim()) params.append("property_id", propertyId.trim());
      if (violationType) params.append("violation_type_id", violationType);
      if (membershipType.trim()) {
        params.append("club_membership_type_code", membershipType.trim());
      }
      params.append("includeMember", "1");

      const response = await api.get(`/getViolationsReport?${params.toString()}`);

      const flattened = (Array.isArray(response.data) ? response.data : []).map(
        (row) => {
          const m = row.member || {};
          const cureByDate = getCureByDate(row.violated_at, row.days_to_cure);
          const resolvedAt = row.resolved_at ? new Date(row.resolved_at) : null;
          const daysDiff = getResolutionDelta(cureByDate, resolvedAt);

          const cureRecords = Array.isArray(row.cure_records)
            ? row.cure_records
            : [];
          const totalRestarts = cureRecords.length;
          const lastRestart = cureRecords[0] || null;
          const lastRestartDate = lastRestart?.recorded_at
            ? new Date(lastRestart.recorded_at).toLocaleDateString()
            : null;
          const lastRestartUser = lastRestart
            ? `${lastRestart.recorded_by_firstname || ""} ${
                lastRestart.recorded_by_lastname || ""
              }`.trim()
            : "";

          const getLatestCureBy = (violated_at, cureRecords, defaultDays) => {
            if (!cureRecords?.length) {
              return getCureByDate(violated_at, defaultDays);
            }
            const latest = cureRecords[0];
            const base = new Date(latest.recorded_at || violated_at);
            const deadline = new Date(base);
            deadline.setDate(base.getDate() + Number(latest.days_to_cure || 0));
            return deadline;
          };

          const newCureByDate = getLatestCureBy(
            row.violated_at,
            cureRecords,
            row.days_to_cure
          );

          return {
            ...row,
            membership_type: m.ClubMembershipTypeCode ?? "",
            member_full_name: getMemberFullName(m),
            member_full_address: getMemberFullAddress(m),
            created_at_display: safeDate(row.created_at),
            letter_created_at_display: safeDate(row.letter_created_at),
            violated_at_display: safeDate(row.violated_at),
            cure_by_display: cureByDate
              ? cureByDate.toLocaleDateString()
              : "N/A",
            new_cure_by_display: newCureByDate
              ? newCureByDate.toLocaleDateString()
              : "N/A",
            resolved_at_display: safeDate(row.resolved_at),
            days_to_cure_display: row.days_to_cure ?? "N/A",
            days_late_display:
              daysDiff == null
                ? "N/A"
                : daysDiff > 0
                ? `${daysDiff} day${daysDiff !== 1 ? "s" : ""} late`
                : daysDiff < 0
                ? `${Math.abs(daysDiff)} day${
                    Math.abs(daysDiff) !== 1 ? "s" : ""
                  } early`
                : "On time",
            total_restarts: totalRestarts,
            last_restart_date: lastRestartDate || "N/A",
            last_restart_by: lastRestartUser || "N/A",
          };
        }
      );

      setFilteredData(flattened);
    } catch (error) {
      console.error("Error fetching filtered violations:", error);
    } finally {
      setLoading(false);
    }
  };

  const columns = useMemo(
    () => [
      { field: "id", headerName: "ID", width: 90 },
      { field: "property_id", headerName: "Member ID", width: 120 },
      { field: "membership_type", headerName: "Membership Type", width: 140 },
      { field: "member_full_name", headerName: "Member Name", width: 180 },
      {
        field: "member_full_address",
        headerName: "Member Full Address",
        width: 320,
      },
      { field: "status", headerName: "Status", width: 140 },
      { field: "violation_type_title", headerName: "Type", width: 200 },
      { field: "violated_at_display", headerName: "Violated At", width: 140 },
      { field: "days_to_cure_display", headerName: "Days to Cure", width: 130 },
      { field: "cure_by_display", headerName: "Original Cure By", width: 160 },
      {
        field: "new_cure_by_display",
        headerName: "New Cure By (Active Deadline)",
        width: 200,
        renderCell: (params) => {
          const original = params.row.cure_by_display;
          const value = params.value || "N/A";
          const changed = original !== value && value !== "N/A";
          return (
            <Typography
              sx={{
                color: changed ? "info.main" : "text.primary",
                fontWeight: changed ? 600 : 400,
              }}
            >
              {value}
            </Typography>
          );
        },
      },
      { field: "total_restarts", headerName: "Cure Restarts", width: 130 },
      { field: "last_restart_date", headerName: "Last Restart", width: 140 },
      { field: "last_restart_by", headerName: "Restarted By", width: 180 },
      { field: "resolved_at_display", headerName: "Resolved At", width: 150 },
      {
        field: "days_late_display",
        headerName: "Resolution Timing",
        width: 180,
        renderCell: (params) => {
          const value = params.value || "N/A";
          const color = value.includes("late")
            ? "error.main"
            : value.includes("early")
            ? "success.main"
            : "text.primary";
          return (
            <Typography sx={{ color, fontWeight: 500 }}>{value}</Typography>
          );
        },
      },
      { field: "created_at_display", headerName: "Created At", width: 140 },
      { field: "letter_action", headerName: "Letter Action", width: 160 },
      { field: "letter_address_type", headerName: "Letter Address", width: 160 },
      {
        field: "letter_created_at_display",
        headerName: "Letter Sent",
        width: 160,
      },
    ],
    []
  );

  const initialColumnVisibility = useMemo(
    () => ({
      member_full_address: !isSmall,
    }),
    [isSmall]
  );

  return (
    <Box
      sx={{
        width: "100%",
        transition: "width 0.3s ease",
      }}
    >
      <Box p={{ xs: 2, md: 3 }}>
        <Card sx={{ ...pageCardSx, overflow: "hidden", mb: 3 }}>
          <Box
            sx={{
              px: { xs: 2, sm: 2.5, md: 3 },
              pt: 2.5,
              pb: 2,
              borderBottom: "1px solid rgba(47,99,173,0.08)",
              backgroundColor: "rgba(47,99,173,0.02)",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: { xs: "flex-start", sm: "center" },
                flexDirection: { xs: "column", sm: "row" },
                gap: 1.25,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.25 }}>
                <Box sx={sectionIconWrapSx}>
                  <FileDownloadOutlinedIcon sx={{ color: "#2f63ad" }} />
                </Box>

                <Box>
                  <Typography
                    variant="h5"
                    sx={{ fontWeight: 700, color: "#223142", lineHeight: 1.2 }}
                  >
                    Violations Export
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#6b778c", mt: 0.5 }}>
                    Filter and review exported violation records with member and cure-date details.
                  </Typography>
                </Box>
              </Box>

              <Chip
                label={`${filteredData.length} record${filteredData.length !== 1 ? "s" : ""}`}
                sx={{
                  borderRadius: "999px",
                  fontWeight: 700,
                  backgroundColor: "rgba(47,99,173,0.10)",
                  color: "#2f63ad",
                }}
              />
            </Box>
          </Box>

          <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
            <Card sx={{ ...softCardSx, p: { xs: 1.5, sm: 2.25 } }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.25,
                  mb: 2,
                }}
              >
                <Box sx={sectionIconWrapSx}>
                  <FilterListIcon sx={{ color: "#2f63ad", fontSize: 20 }} />
                </Box>

                <Box>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 700, color: "#223142", lineHeight: 1.2 }}
                  >
                    Filters
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#6b778c", mt: 0.3 }}>
                    Narrow the report by member, type, status, and date range.
                  </Typography>
                </Box>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="Member/Property ID"
                    type="text"
                    InputLabelProps={{ shrink: true }}
                    value={propertyId}
                    onChange={(e) => setPropertyId(e.target.value)}
                    sx={textFieldSx}
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <FormControl fullWidth sx={textFieldSx}>
                    <InputLabel>Membership Type</InputLabel>
                    <Select
                      value={membershipType}
                      label="Membership Type"
                      onChange={(e) => setMembershipType(e.target.value)}
                      disabled={membershipTypeLoading}
                    >
                      <MenuItem value="">All</MenuItem>
                      {membershipTypeOptions.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={3}>
                  <Autocomplete
                    fullWidth
                    options={violationTypeOptions}
                    getOptionLabel={(opt) => opt.label || ""}
                    value={
                      violationTypeOptions.find((opt) => opt.value === violationType) ||
                      null
                    }
                    onChange={(e, newValue) =>
                      setViolationType(newValue ? newValue.value : "")
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Violation Type"
                        variant="outlined"
                        sx={textFieldSx}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <FormControl fullWidth sx={textFieldSx}>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={status}
                      label="Status"
                      onChange={(e) => setStatus(e.target.value)}
                    >
                      <MenuItem value="">All</MenuItem>
                      {statuses.map((s) => (
                        <MenuItem key={s.id} value={s.statusname}>
                          {s.statusname}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Start Date"
                    type="date"
                    InputLabelProps={{ shrink: true }}
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    sx={textFieldSx}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="End Date"
                    type="date"
                    InputLabelProps={{ shrink: true }}
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    sx={textFieldSx}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={handleSearch}
                    startIcon={<SearchIcon />}
                    sx={primaryButtonSx}
                  >
                    Search
                  </Button>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={handleReset}
                    startIcon={<RestartAltIcon />}
                    sx={secondaryButtonSx}
                  >
                    Reset Filters
                  </Button>
                </Grid>
              </Grid>
            </Card>
          </Box>
        </Card>

        <Box mt={3}>
          {loading ? (
            <Card sx={{ ...pageCardSx, p: 3 }}>
              <Typography sx={{ color: "#6b778c" }}>Loading...</Typography>
            </Card>
          ) : filteredData.length > 0 ? (
            <Card sx={{ ...pageCardSx, overflow: "hidden" }}>
              <Box
                sx={{
                  px: { xs: 2, sm: 2.5, md: 3 },
                  pt: 2.25,
                  pb: 1.5,
                  borderBottom: "1px solid rgba(47,99,173,0.08)",
                  backgroundColor: "rgba(47,99,173,0.02)",
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 700, color: "#223142" }}>
                  Results
                </Typography>
                <Typography variant="body2" sx={{ color: "#6b778c", mt: 0.4 }}>
                  Review and export filtered violation report data below.
                </Typography>
              </Box>

              <Box ref={printRef} sx={{ p: { xs: 1, sm: 1.5, md: 2 } }}>
                <Paper
                  elevation={0}
                  sx={{
                    borderRadius: "14px",
                    overflow: "hidden",
                    border: "1px solid rgba(47,99,173,0.08)",
                  }}
                >
                  <DataGrid
                    rows={filteredData}
                    columns={columns}
                    getRowId={(r) =>
                      r.id ??
                      `${r.property_id}-${r.created_at}-${r.violation_type_title}`
                    }
                    autoHeight
                    disableRowSelectionOnClick
                    slots={{ toolbar: GridToolbar }}
                    slotProps={{
                      toolbar: {
                        showQuickFilter: true,
                        quickFilterProps: { debounceMs: 300 },
                        sx: {
                          p: 1.5,
                          borderBottom: "1px solid rgba(47,99,173,0.08)",
                          backgroundColor: "rgba(47,99,173,0.02)",
                        },
                      },
                    }}
                    initialState={{
                      pagination: {
                        paginationModel: { pageSize: 25, page: 0 },
                      },
                      columns: {
                        columnVisibilityModel: initialColumnVisibility,
                      },
                    }}
                    pageSizeOptions={[10, 25, 50, 100]}
                    getRowHeight={() => "auto"}
                    sx={{
                      border: "none",
                      "& .MuiDataGrid-columnHeaders": {
                        backgroundColor: "rgba(47,99,173,0.03)",
                        color: "#223142",
                        fontWeight: 700,
                      },
                      "& .MuiDataGrid-cell": {
                        py: 1.25,
                        alignItems: "center",
                      },
                      "& .MuiDataGrid-toolbarContainer": {
                        gap: 1,
                        flexWrap: "wrap",
                      },
                    }}
                  />
                </Paper>

                <Typography
                  variant="body2"
                  sx={{
                    mt: 2,
                    textAlign: "left",
                    fontWeight: 700,
                    color: "#223142",
                  }}
                >
                  Total Records: {filteredData.length}
                </Typography>
              </Box>
            </Card>
          ) : (
            <Card sx={{ ...pageCardSx, p: 3, textAlign: "center" }}>
              <Typography variant="body1" sx={{ fontWeight: 600, color: "#223142" }}>
                No results to display
              </Typography>
              <Typography variant="body2" sx={{ color: "#6b778c", mt: 0.75 }}>
                Apply filters and run a search to view exported violations.
              </Typography>
            </Card>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default ViolationsExportPage;